import type { Activity } from "@/lib/activity";
import { Item, ItemContent, ItemDescription, ItemFooter, ItemMedia, ItemTitle } from "@/components/ui/item";
import { Button } from "@/components/ui/button";
import { useCallback, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { format, formatRelative } from "date-fns";
import { CheckIcon, CircleCheck, MapPin, Timer, User, Users } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface ActivityActionsProps {
  activity: Activity,
  refreshActivities: () => void,
};

export function ActivityItem({ activity, refreshActivities }: ActivityActionsProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchWithAuth } = useAuth();

  const book = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWithAuth("POST", `/activities/${activity.id}/book`);
      toast.success(`Booked ${activity.name}`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const schedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("POST", `/activities/${activity.id}/schedule`);
      toast.success(`Scheduled ${activity.name} for booking`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const cancelScheduledBooking = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWithAuth("DELETE", `/activities/${activity.id}/schedule`);
      toast.success(`${activity.name} is no longer scheduled for booking`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const cancelBooking = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWithAuth("DELETE", `/bookings/${activity.bookingId}`);
      toast.success(`Cancelled booking for ${activity.name}`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const joinWaitlist = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWithAuth("POST", `/activities/${activity.id}/waitlist`);
      toast.success(`You're on the waitlist for ${activity.name}`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const leaveWaitlist = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWithAuth("DELETE", `/activities/${activity.id}/waitlist`);
      toast.success(`You've left the waitlist for ${activity.name}`);
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred");
    } finally {
      setLoading(false);
      refreshActivities();
    }
  }, []);

  const formatPounds = useCallback((pence: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(pence / 100);
  }, []);

  const spaces = activity.capacity - activity.bookings;

  const buttonText = {
    "SCHEDULED": "Cancel Scheduled Booking",
    "PREBOOK": "Pre-book Activity",
    "BOOK": "Book Activity",
    "CANCEL_BOOKING": "Cancel Booking",
    "JOIN_WAITLIST": "Join Waitlist",
    "LEAVE_WAITLIST": "Leave Waitlist",
    "FULL": "This activity is full",
  }[activity.status] ?? "This activity can't be booked";

  const buttonVariant = ({
    "SCHEDULED": "destructive",
    "PREBOOK": "default",
    "BOOK": "default",
    "CANCEL_BOOKING": "destructive",
    "JOIN_WAITLIST": "outline",
    "LEAVE_WAITLIST": "destructive",
    "FULL": "outline"
  }[activity.status] ?? "outline") as "default" | "destructive" | "outline";

  const actions: Record<string, () => void> = {
    "SCHEDULED": cancelScheduledBooking,
    "PREBOOK": schedule,
    "BOOK": book,
    "CANCEL_BOOKING": cancelBooking,
    "JOIN_WAITLIST": joinWaitlist,
    "LEAVE_WAITLIST": leaveWaitlist,
  };

  const firstBookableAt = new Date(activity.firstBookableAt);
  const lastBookableAt = new Date(activity.lastBookableAt);

  return (
    <Item variant="outline" className="items-start p-0 justify-between">
      <div className="flex items-start gap-4 p-4 w-full">
        <ItemMedia className="text-lg leading-snug text-gray-800">
          {format(activity.startsAt, "HH:mm")}
        </ItemMedia>
        <div className="flex flex-wrap justify-between gap-4 w-full">
          <ItemContent>
            <ItemTitle className="text-lg">
              {activity.name}
            </ItemTitle>
            {!!activity.location &&
              <ItemDescription className="flex items-center gap-1">
                <MapPin size={12} />
                {activity.location}
              </ItemDescription>
            }
            {!!activity.instructors.length &&
              <ItemDescription className="flex items-center gap-1">
                {activity.instructors.length > 1 ? <Users size={12} /> : <User size={12} />}
                {activity.instructors.join(", ")}
              </ItemDescription>
            }
          </ItemContent>
          <ItemContent className="flex grow shrink basis-auto">
            <ItemTitle className="text-lg">
              {activity.price ? formatPounds(activity.price) : "Free"}
            </ItemTitle>
            <ItemDescription className="flex items-center gap-1">
              <Timer size={12} />
              {(new Date() < firstBookableAt) && <>Opens {formatRelative(firstBookableAt, new Date())}</>}
              {(firstBookableAt < new Date() && new Date() < lastBookableAt) && <>Closes {formatRelative(lastBookableAt, new Date())}</>}
              {(lastBookableAt < new Date()) && <>Closed {formatRelative(lastBookableAt, new Date())}</>}
            </ItemDescription>
            {activity.status in actions && <ItemDescription className={cn("flex items-center gap-1", { "text-red-500": spaces > 0 && spaces / activity.capacity < 0.5 })}>
              {(spaces === 0 && activity.waitlistCount > 1) && <><Users size={12} />{activity.waitlistCount} waiting</>}
              {(spaces === 0 && activity.waitlistCount === 1) && <><User size={12} />{activity.waitlistCount} waiting</>}
              {spaces === 1 && <><User size={12} />1 space left</>}
              {spaces > 1 && <><Users size={12} />{spaces} spaces left</>}
            </ItemDescription>}
          </ItemContent>
        </div>
      </div>
      <ItemFooter className="bg-muted/50 border-t flex flex-col gap-4 group-data-[size=sm]/card:p-3 items-center p-4 rounded-b-xl">
        {activity.status === "CANCEL" &&
          <Alert variant="default" className="[&>svg]:text-green-500">
            <CircleCheck />
            <AlertDescription className="text-green-500">You're booked on to this class!</AlertDescription>
          </Alert>
        }
        {activity.status === "SCHEDULED" &&
          <Alert variant="default" className="[&>svg]:text-green-500">
            <CircleCheck />
            <AlertDescription className="text-green-500">We'll try and book you onto this class when booking opens {formatRelative(firstBookableAt, new Date())}.</AlertDescription>
          </Alert>
        }
        {activity.status === "LEAVE_WAITLIST" &&
          <Alert variant="default" className="[&>svg]:text-green-500">
            <CircleCheck />
            <AlertDescription className="text-green-500">You're on the waitlist. We'll try and book you on when a space becomes free.</AlertDescription>
          </Alert>
        }
        <Button onClick={actions[activity.status]} disabled={loading || !(activity.status in actions)} variant={buttonVariant} className="w-full disabled:pointer-events-auto disabled:cursor-not-allowed">
          {loading && <Spinner data-icon="inline-start" />}
          {buttonText}
        </Button>
      </ItemFooter>
    </Item>
  );
}